import React, { useContext, useEffect, useReducer } from 'react';
import { Store } from '../Store';
import api, { ADMIN_URL } from '../api';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Col,
  Container,
  NavDropdown,
  Row,
  Table,
} from 'react-bootstrap';
import { getError } from '../Util';
import { Helmet } from 'react-helmet-async';
import LoadingBox from '../components/LoadingBox';
import MessageBox from '../components/MeaasgeBox';
import CardHeader from '../components/CardHeadder';
import Swal from 'sweetalert2';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, orders: action.payload };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'DELETE_REQUEST':
      return { ...state, loadingDelete: true };
    case 'DELETE_SUCCESS':
      return {
        ...state,
        loadingDelete: false,
        orders: state.orders.filter((o) => o._id !== action.payload),
      };
    case 'DELETE_FAIL':
      return { ...state, loadingDelete: false };
    default:
      return state;
  }
};

export default function OrderListScreen() {
  const navigate = useNavigate();
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [{ loading, error, orders, loadingDelete }, dispatch] = useReducer(
    reducer,
    {
      loading: true,
      orders: [],
      error: '',
    }
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data } = await api.get('/api/orders', {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };
    if (userInfo && userInfo.isAdmin) {
      fetchData();
    } else {
      navigate('/login');
    }
  }, [userInfo, navigate]);

  const deleteHandler = async (orderId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won’t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          dispatch({ type: 'DELETE_REQUEST' });
          await api.delete(`/api/orders/${orderId}`, {
            headers: { Authorization: `Bearer ${userInfo.token}` },
          });
          dispatch({ type: 'DELETE_SUCCESS', payload: orderId });
          Swal.fire('Deleted!', 'The order has been deleted.', 'success');
        } catch (error) {
          dispatch({ type: 'DELETE_FAIL' });
          Swal.fire('Error!', getError(error), 'error');
        }
      }
    });
  };

  const { dispatch: ctxDispatch } = useContext(Store);

  const signoutHandler = () => {
    ctxDispatch({ type: 'USER_SIGNOUT' });
    localStorage.removeItem('userInfo');
    localStorage.removeItem('shippingAddress');
    localStorage.removeItem('paymentMethod');
    window.location.href = '/signin';
  };

  return (
    <div>
      <CardHeader />
      <Container fluid="md" className="mt-5 py-5">
        <Helmet>
          <title>Order Management</title>
        </Helmet>
        <Row className="align-items-center mb-4">
          <Col md={8}>
            <h1 className="fs-2 mb-4" style={{ fontSize: '2rem' }}>
              Order Management
            </h1>
          </Col>
          <Col md={4}>
            <div className="d-flex justify-content-end align-items-center">
              {userInfo ? (
                <NavDropdown
                  title={<span className="text-black">{userInfo.name}</span>}
                  id="collasible-nav-dropdown"
                >
                  <NavDropdown.Item as={Link} to="/profile">
                    <span className="text-gray">User Profile</span>
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <Link
                    className="dropdown-item"
                    to="#signout"
                    onClick={signoutHandler}
                  >
                    <span className="text-gray">Signout</span>
                  </Link>
                </NavDropdown>
              ) : (
                <a className="nav-link text-black" href={ADMIN_URL}>
                  <span className="text-black">Signin</span>
                </a>
              )}

              {userInfo && userInfo.isAdmin && (
                <NavDropdown
                  title={<span className="text-black">Admin</span>}
                  id="admin-nav-dropdown"
                  className="ms-3" // Adds left margin (gap) between user and admin
                >
                  <NavDropdown.Item as={Link} to="/admin/products">
                    <span className="text-gray">Products</span>
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/orders">
                    <span className="text-gray">Orders</span>
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/fabrics">
                    <span className="text-gray">Fabrics</span>
                  </NavDropdown.Item>
                </NavDropdown>
              )}
            </div>
          </Col>
        </Row>
        <div className="content-wrapper">
          {loading ? (
            <LoadingBox />
          ) : error ? (
            <MessageBox variant="danger">{error}</MessageBox>
          ) : (
            <>
              {loadingDelete && <LoadingBox />}
              <div className="table-responsive overflow-auto">
                <Table
                  striped
                  bordered
                  hover
                  className="align-middle shadow-sm"
                >
                  <thead className="table-dark">
                    <tr>
                      <th className="px-3">ORDER ID</th>
                      <th className="px-3">ORDER DATE</th>
                      <th className="px-3">PRODUCTS</th>
                      <th className="px-3">CUSTOMER</th>
                      <th className="px-3">WHATSAPP</th>
                      <th className="px-3">ADDRESS</th>
                      <th className="px-3">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order._id}>
                        <td className="fw-semibold text-wrap">
                          {order.orderCode}
                        </td>

                        <td>
                          {new Date(order.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          {order.orderItems.map((item, index) => (
                            <div key={index}>
                              <strong>Code:</strong> {item.code}
                              <br />
                              <strong>Product:</strong> {item.name}
                              <br />
                              <strong>Qty:</strong> {item.quantity}
                              <br />
                              {index < order.orderItems.length - 1 && <hr />}
                            </div>
                          ))}
                        </td>
                        <td>{order.shippingAddress.fullName}</td>
                        <td>{order.shippingAddress.whatsapp}</td>
                        <td>{order.shippingAddress.address}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              onClick={() =>
                                navigate(`/order/${order.orderCode}`)
                              }
                            >
                              View
                            </Button>
                            <Button
                              variant="danger"
                              onClick={() => deleteHandler(order._id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </div>
      </Container>
    </div>
  );
}
